// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {appName} from '@/composables/useGlobalVariables.js';  

import AppHome from '../views/home/LandingPage.vue';
import UserLogin from '../views/account/UserLogin.vue';
import UserRegister from '../views/account/UserRegister.vue';
import UserProfile from '../views/account/UserProfile.vue';
import ForgotPassword from '../views/account/ForgotPassword.vue';
import UserUpdateProfile from '../views/account/UserUpdateProfile.vue';
import UserChangePassword from '../views/account/UserChangePassword.vue';
import UserUpdateProfilePicture from '../views/account/UserUpdateProfilePicture.vue';
import UserAccountDelete from '../views/account/UserAccountDelete.vue';
import UserPlanUpgrade from '../views/account/UserPlanUpgrade.vue';
import UserSubscriptionManagement from '../views/account/UserSubscriptionManagement.vue';
import NotFound from '@/views/404/NotFound.vue';
import PricingPage from '../views/home/PricingPage.vue';
import PrivacyPolicy from '@/views/legal/PrivacyPolicy.vue';
import TermsOfService from '@/views/legal/TermsOfService.vue';

const routes = [
  { path: '/', component: AppHome, meta: { title: `${appName} | AI YouTube Video Ideas Generator` } },
  { path: '/ai-youtube-video-ideas-generator', component: AppHome, meta: { requiresAuth: true, title: `${appName} | AI YouTube Video Ideas Generator` } },
  { path: '/login', component: UserLogin, meta: { title: `Sign In | ${appName}` } },
  { path: '/register', component: UserRegister, meta: { title: `Create Account | ${appName}` } },
  { path: '/forgot-password', component: ForgotPassword, meta: { title: `Reset Password | ${appName}` } },
  // Protected Routes
  { path: '/profile', component: UserProfile, meta: { requiresAuth: true, title: `Profile | ${appName}` } },
  { path: '/update-profile', component: UserUpdateProfile, meta: { requiresAuth: true, title: `Update Profile | ${appName}` } },
  { path: '/update-profile-picture', component: UserUpdateProfilePicture, meta: { requiresAuth: true, title: `Update Profile Picture | ${appName}` } },
  { path: '/change-password', component: UserChangePassword, meta: { requiresAuth: true, title: `Change Password | ${appName}` } },
  { path: '/delete-account', component: UserAccountDelete, meta: { requiresAuth: true, title: `Delete Account | ${appName}` } },
  { path: '/upgrade', component: UserPlanUpgrade, meta: { requiresAuth: true, title: `Upgrade Plan | ${appName}` } },
  {
    path: '/pricing',
    name: 'Pricing',
    component: PricingPage
  },
  {
    path: '/subscription-management',
    component: UserSubscriptionManagement,
    meta: { requiresAuth: true, title: `Manage Subscription | ${appName}` }
  },
  {
    path: '/payment-history',
    name: 'PaymentHistory',
    component: () => import('@/views/account/PaymentHistory.vue'),
    meta: { requiresAuth: true }
  },
  // Legal pages
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { 
      title: 'Privacy Policy - TrendzTube',
      description: 'TrendzTube Privacy Policy - Learn how we collect, use, and protect your personal information when using our AI-powered YouTube video idea generator.'
    }
  },
  {
    path: '/terms',
    name: 'TermsOfService', 
    component: TermsOfService,
    meta: { 
      title: 'Terms of Service - TrendzTube',
      description: 'TrendzTube Terms of Service - Read our terms and conditions for using our AI-powered YouTube video idea generation platform.'
    }
  },

  // Catch-all
  { path: '/:pathMatch(.*)*', component: NotFound, meta: { title: `404 Not Found | ${appName}` } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

// Wait for Firebase to confirm auth state (fix for refresh issues)
function getCurrentUser() {
  return new Promise((resolve, reject) => {
    const removeListener = onAuthStateChanged(getAuth(), user => {
      removeListener();
      resolve(user);
    }, reject);
  });
}

// Navigation guard

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const user = await getCurrentUser();

  // Redirect signed-in users from '/' to '/ai-youtube-video-ideas-generator'
  if (to.path === '/' && user) {
    next('/ai-youtube-video-ideas-generator');
    return;
  }

  if (requiresAuth && !user) {
    next('/login');
  } else {
    next();
  }
});

// Add afterEach hook for dynamic title
router.afterEach((to) => {
  const defaultTitle = `${appName}`;
  document.title = to.meta && to.meta.title ? to.meta.title : defaultTitle;
});

export default router;
